import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 15_000; // 15s — chat leads move fast, we want to know sooner than inbox items
const STORAGE_LAST_TOTAL = "sb_chat_notifier_last_total";
const STORAGE_MUTE = "sb_chat_notifier_mute";
const STORAGE_LAST_ALERT = "sb_chat_notifier_last_alert_at";
const MIN_ALERT_INTERVAL_MS = 30_000; // dedupe across open tabs

/**
 * useChatNotifier
 * ---------------
 * Sibling of `useApprovalNotifier`. Polls `/admin/chat-leads/count` every
 * 15s and, whenever the total chat-session count strictly increases (a
 * new visitor started chatting with Sable), fires:
 *   1. A three-tone chime via Web Audio API — distinct from the approval
 *      notifier so admins can tell them apart at a glance.
 *   2. A native browser notification (needs one-time permission — the
 *      approval notifier already handles the ask).
 *
 * Mounted globally inside `ProtectedAdminRoute` alongside the approval
 * notifier so a chime rings on any admin page the moment a new lead
 * lands, even while the studio is between locations.
 */
const useChatNotifier = (enabled = true) => {
  const [counts, setCounts] = useState({ total: 0, latest_at: null });
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(STORAGE_MUTE) === "1"; } catch { return false; }
  });
  const audioCtxRef = useRef(null);
  const inflightRef = useRef(false);

  const playChime = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      // Three ascending soft bells — 780 / 1040 / 1320 Hz — an octave-ish
      // higher and faster than the inbox chime so admins can tell the
      // sounds apart without looking.
      [
        { freq: 780, start: now, dur: 0.14 },
        { freq: 1040, start: now + 0.11, dur: 0.14 },
        { freq: 1320, start: now + 0.22, dur: 0.20 },
      ].forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.14, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur + 0.02);
      });
    } catch {
      /* AudioContext failing pre-interaction is expected browser behaviour */
    }
  }, []);

  const showBrowserNotification = useCallback((delta) => {
    try {
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      const notif = new Notification(
        `${delta} new chat${delta === 1 ? "" : "s"} with Sable`,
        {
          body: "A visitor just started a chat — jump in before they leave.",
          tag: "sb-chat-notifier",
          icon: "/brand/sb-logo.svg",
          silent: false,
        },
      );
      notif.onclick = () => {
        try { window.focus(); } catch (_e) { /* noop */ }
        window.location.href = "/admin/chat-leads";
        notif.close();
      };
    } catch {
      /* ignore */
    }
  }, []);

  const alertIfNew = useCallback(
    (next) => {
      if (muted) return;
      let last = 0;
      try {
        last = parseInt(localStorage.getItem(STORAGE_LAST_TOTAL) || "0", 10) || 0;
      } catch { last = 0; }
      const delta = next.total - last;
      if (delta > 0 && last > 0) {
        // last > 0 skips the first-load fire — on a fresh sign-in we
        // seed the baseline without ringing for existing history.
        const lastAlertAt = parseInt(localStorage.getItem(STORAGE_LAST_ALERT) || "0", 10) || 0;
        if (Date.now() - lastAlertAt > MIN_ALERT_INTERVAL_MS) {
          playChime();
          showBrowserNotification(delta);
          try { localStorage.setItem(STORAGE_LAST_ALERT, String(Date.now())); } catch { /* noop */ }
        }
      }
      try { localStorage.setItem(STORAGE_LAST_TOTAL, String(next.total)); } catch { /* noop */ }
    },
    [muted, playChime, showBrowserNotification],
  );

  const poll = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const token = localStorage.getItem("sb_admin_token") || "";
      if (!token) return;
      const { data } = await axios.get(`${API}/admin/chat-leads/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCounts(data);
      alertIfNew(data);
    } catch {
      /* silent — 401 handled by route guard */
    } finally {
      inflightRef.current = false;
    }
  }, [alertIfNew]);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, poll]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_MUTE, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  }, []);

  return { counts, muted, toggleMute, refresh: poll };
};

export default useChatNotifier;
